import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { goodsReceiptsApi } from "../../../api/inventory/goods_receipts";
import { purchasesApi } from "../../../api/inventory/purchases";
import { inventoryStoragesApi } from "../../../api/inventory_storages";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import {
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { Button, TextField, MenuItem, Select, FormControl, InputLabel } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import toast from "react-hot-toast";
import { axiosWithToast } from "../../../components/Utils";
import { AddProductDialog } from "../../../components/utils/AddProductDialog";

export const GoodsReceipt = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const purchaseId = searchParams.get("purchase_id");
  const isNew = id === "new";
  
  const [goodsReceipt, setGoodsReceipt] = useState(null);
  const [purchase, setPurchase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [storages, setStorages] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [updatingItem, setUpdatingItem] = useState(null);
  const [addProductDialogOpen, setAddProductDialogOpen] = useState(false);

  const [formData, setFormData] = useState({
    receipt_date: new Date().toISOString().split("T")[0],
    comment: "",
    purchase_id: purchaseId || null,
    items: [],
  });

  useEffect(() => {
    if (isNew) {
      fetchStorages();
      if (purchaseId) {
        fetchPurchase();
      }
      setLoading(false);
    } else {
      fetchGoodsReceipt();
      fetchStorages();
    }
  }, [id, purchaseId, isNew]);

  const fetchStorages = async () => {
    try {
      const response = await inventoryStoragesApi.getInventoryStorages({ active: true });
      if (response?.success) {
        setStorages(response.data || []);
      } else if (Array.isArray(response)) {
        setStorages(response);
      }
    } catch (err) {
      console.error("Error fetching storages:", err);
    }
  };

  const fetchPurchase = async () => {
    try {
      const response = await purchasesApi.getPurchase(purchaseId);
      if (response?.data?.success) {
        const purchaseData = response.data.data;
        setPurchase(purchaseData);
        
        // Pre-fill items from purchase
        if (purchaseData.items && purchaseData.items.length > 0) {
          const items = purchaseData.items.map((item) => ({
            purchase_item_id: item.id,
            product_id: item.product_id,
            product: item.product,
            quantity_expected: item.quantity,
            quantity_received: item.quantity,
            storage_id: null,
            unit_id: item.unit_id,
            comment: "",
          }));
          setFormData((prev) => ({ ...prev, items }));
        }
      }
    } catch (err) {
      console.error("Error fetching purchase:", err);
      toast.error("Kunne ikke hente innkjøp");
    }
  };

  const fetchGoodsReceipt = async () => {
    try {
      setLoading(true);
      const response = await goodsReceiptsApi.getGoodsReceipt(id);
      if (response?.data?.success) {
        const receiptData = response.data.data;
        setGoodsReceipt(receiptData);
        setFormData({
          receipt_date: receiptData.receipt_date ? new Date(receiptData.receipt_date).toISOString().split("T")[0] : new Date().toISOString().split("T")[0],
          comment: receiptData.comment || "",
          purchase_id: receiptData.purchase_id || null,
          items: receiptData.items || [],
        });
        if (receiptData.purchase_id) {
          fetchPurchaseForReceipt(receiptData.purchase_id);
        }
      } else {
        setError("Kunne ikke hente varemottak");
      }
    } catch (err) {
      console.error("Error fetching goods receipt:", err);
      setError("Kunne ikke hente varemottak");
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchaseForReceipt = async (purchaseId) => {
    try {
      const response = await purchasesApi.getPurchase(purchaseId);
      if (response?.data?.success) {
        setPurchase(response.data.data);
      }
    } catch (err) {
      console.error("Error fetching purchase:", err);
    }
  };

  const handleSave = async () => {
    if (!formData.receipt_date) {
      toast.error("Mottaksdato er påkrevd");
      return;
    }

    if (!formData.items || formData.items.length === 0) {
      toast.error("Legg til minst ett produkt");
      return;
    }

    // Validate all items have product and storage
    for (const item of formData.items) {
      if (!item.product_id) {
        toast.error("Alle produkter må ha et produkt valgt");
        return;
      }
      if (!item.storage_id) {
        toast.error("Alle produkter må ha et lager valgt");
        return;
      }
    }

    try {
      if (isNew) {
        const response = await goodsReceiptsApi.createGoodsReceipt(
          {
            receipt_date: formData.receipt_date,
            comment: formData.comment || "",
            purchase_id: formData.purchase_id || null,
            items_attributes: formData.items.map((item) => ({
              purchase_item_id: item.purchase_item_id || null,
              product_id: item.product_id,
              quantity_received: item.quantity_received || 0,
              quantity_expected: item.quantity_expected || 0,
              storage_id: item.storage_id || null,
              unit_id: item.unit_id || null,
              comment: item.comment || "",
            })),
          },
          purchaseId
        );

        if (response?.data?.success) {
          toast.success("Varemottak opprettet");
          navigate(`/dashboard/inventory/goods-receipts/${response.data.data.id}`);
        } else {
          toast.error("Kunne ikke opprette varemottak");
        }
      } else {
        const response = await goodsReceiptsApi.updateGoodsReceipt(id, {
          receipt_date: formData.receipt_date,
          comment: formData.comment || "",
          items_attributes: formData.items.map((item) => ({
            id: item.id,
            purchase_item_id: item.purchase_item_id || null,
            product_id: item.product_id,
            quantity_received: item.quantity_received || 0,
            quantity_expected: item.quantity_expected || 0,
            storage_id: item.storage_id || null,
            unit_id: item.unit_id || null,
            comment: item.comment || "",
          })),
        });

        if (response?.data?.success) {
          toast.success("Varemottak oppdatert");
          fetchGoodsReceipt();
        } else {
          toast.error("Kunne ikke oppdatere varemottak");
        }
      }
    } catch (error) {
      console.error("Error saving goods receipt:", error);
      toast.error("Kunne ikke lagre varemottak");
    }
  };

  const handleAddItem = () => {
    setAddProductDialogOpen(true);
  };

  const handleProductSelected = async (product, formData) => {
    if (!product) {
      toast.error("Velg et produkt");
      return;
    }

    const newItem = {
      purchase_item_id: null,
      product_id: product.id,
      product: product,
      quantity_expected: formData.quantity || 0,
      quantity_received: formData.quantity || 0,
      storage_id: formData.storage_id || null,
      unit_id: product.unit_id || null,
      comment: formData.comment || "",
    };
    
    setFormData((prev) => ({
      ...prev,
      items: [...(prev.items || []), newItem],
    }));
    setAddProductDialogOpen(false);
  };


  const handleRemoveItem = (index) => {
    if (!confirm("Er du sikker på at du vil fjerne denne linjen?")) return;
    const updatedItems = formData.items.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, items: updatedItems }));
  };

  if (loading) {
    return (
      <>
        <DashboardHeader title={isNew ? "Nytt varemottak" : "Varemottak"} subtitle="Laster..." />
        <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
          <ResourceLoading />
        </div>
      </>
    );
  }

  if (error && !isNew) {
    return (
      <>
        <DashboardHeader title="Varemottak" subtitle="" />
        <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
          <ResourceError errors={[error]} />
        </div>
      </>
    );
  }

  return (
    <>
      <DashboardHeader
        title={isNew ? "Nytt varemottak" : `Varemottak ${goodsReceipt?.receipt_number || ""}`}
        subtitle={purchase ? `Fra innkjøp ${purchase.purchase_number}` : ""}
        actions={[
          {
            component: null,
            label: "Tilbake",
            description: "",
            path: purchaseId ? `/dashboard/inventory/purchases/${purchaseId}` : "/dashboard/inventory/purchases",
            action: null,
            icon: ArrowLeftIcon,
            textColor: "text-gray-700",
            bgColor: "bg-white",
            hidden: false,
            menu: false,
          },
        ]}
      />
      <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 pb-24">
        <div className="mt-8 space-y-6">
          {/* Form Details */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mottaksdato *</label>
                <TextField
                  fullWidth
                  type="date"
                  variant="outlined"
                  size="small"
                  value={formData.receipt_date}
                  onChange={(e) => setFormData({ ...formData, receipt_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </div>
              {purchase && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Innkjøp</label>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={purchase.purchase_number || ""}
                    disabled
                  />
                </div>
              )}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Kommentar</label>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  size="small"
                  value={formData.comment || ""}
                  onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Mottatte produkter</h3>
              <Button
                variant="contained"
                startIcon={<PlusIcon className="h-4 w-4" />}
                onClick={handleAddItem}
              >
                Legg til produkt
              </Button>
            </div>

            {formData.items && formData.items.length > 0 ? (
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Produkt</TableCell>
                    <TableCell>Forventet</TableCell>
                    <TableCell>Mottatt</TableCell>
                    <TableCell>Lager</TableCell>
                    <TableCell>Kommentar</TableCell>
                    <TableCell align="right">Handlinger</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        {item.product?.displayTitle || item.product?.title || item.title || "-"}
                      </TableCell>
                      <TableCell>{item.quantity_expected || 0}</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={item.quantity_received || 0}
                          onChange={(e) => {
                            const updated = [...formData.items];
                            updated[index] = { ...item, quantity_received: parseFloat(e.target.value) || 0 };
                            setFormData((prev) => ({ ...prev, items: updated }));
                          }}
                          inputProps={{ min: 0, step: 0.01 }}
                          sx={{ width: 100 }}
                        />
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                          <Select
                            value={item.storage_id || ""}
                            onChange={(e) => {
                              const updated = [...formData.items];
                              updated[index] = { ...item, storage_id: e.target.value || null };
                              setFormData((prev) => ({ ...prev, items: updated }));
                            }}
                            displayEmpty
                          >
                            <MenuItem value="">Velg lager</MenuItem>
                            {storages.map((storage) => (
                              <MenuItem key={storage.id} value={storage.id}>
                                {storage.title}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={item.comment || ""}
                          onChange={(e) => {
                            const updated = [...formData.items];
                            updated[index] = { ...item, comment: e.target.value };
                            setFormData((prev) => ({ ...prev, items: updated }));
                          }}
                          placeholder="Kommentar"
                          sx={{ width: 150 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <button
                          onClick={() => handleRemoveItem(index)}
                          className="text-red-600 hover:text-red-900"
                          title="Slett"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500 mb-4">Ingen produkter lagt til ennå</p>
                <Button variant="contained" onClick={handleAddItem}>
                  Legg til første produkt
                </Button>
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              variant="contained"
              size="large"
              startIcon={<CheckCircleIcon className="h-5 w-5" />}
              onClick={handleSave}
            >
              {isNew ? "Opprett varemottak" : "Lagre endringer"}
            </Button>
          </div>
        </div>
      </div>

      {/* Add Product Dialog */}
      <AddProductDialog
        open={addProductDialogOpen}
        onClose={() => setAddProductDialogOpen(false)}
        onProductSelected={handleProductSelected}
        multiple={false}
        title="Legg til produkt"
        renderAdditionalFields={(selectedProducts, formData, setFormData) => {
          if (selectedProducts.length === 0) return null;
          return (
            <div className="space-y-4 mt-4">
              <TextField
                fullWidth
                label="Forventet antall"
                type="number"
                value={formData.quantity || 0}
                onChange={(e) => setFormData({ ...formData, quantity: parseFloat(e.target.value) || 0 })}
                inputProps={{ min: 0, step: 0.01 }}
              />
              <FormControl fullWidth>
                <InputLabel>Lager</InputLabel>
                <Select
                  value={formData.storage_id || ""}
                  label="Lager"
                  onChange={(e) => setFormData({ ...formData, storage_id: e.target.value || null })}
                >
                  <MenuItem value="">Velg lager</MenuItem>
                  {storages.map((storage) => (
                    <MenuItem key={storage.id} value={storage.id}>
                      {storage.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Kommentar"
                multiline
                rows={2}
                value={formData.comment || ""}
                onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
              />
            </div>
          );
        }}
        initialFormData={{ quantity: 0, storage_id: null, comment: "" }}
      />
    </>
  );
};

